/**
 * WHAT THE REVIEW SCREEN IS ALLOWED TO SAY, as a pure function of four facts.
 *
 * Its own module rather than a helper inside `Book.tsx`, and the reason is
 * testability rather than tidiness: importing `Book.tsx` pulls in the Apollo
 * client and `config/env`, which refuses to load outside a `--mode` build. So
 * the mapping that decides the most consequential sentence on this surface
 * could not be asserted directly from a unit test at all — and it had no
 * coverage of any kind until two independent passes found the same hole.
 */
import type { BookingFailure } from "../../features/booking/operations";
import type { BookingState } from "../../features/booking/useBooking";
import type { HoldState } from "../../features/booking/useHold";
import type { ReviewState } from "./StepReview";

/**
 * Which recovery a documented commit failure maps to.
 *
 * Kept as a total function over the union so a new failure kind is a TYPE ERROR
 * here rather than a silent fall-through to "default" — which is exactly how
 * a lost slot came to render as a normal, confirmable review screen.
 */
function bookingFailureToReviewState(failure: BookingFailure): ReviewState {
  switch (failure.kind) {
    case "slot-unavailable":
      return "taken";
    case "hold-gone":
      return "expired";
    case "settings-changed":
      return "settings";
    case "payment-mode-unavailable":
    case "payment-client-unsupported":
      return "paymentUnavailable";
    // Back to Time for a fresh hold, which also re-reads the terms.
    case "payment-terms-changed":
      return "settings";
    case "account-required":
      return "accountRequired";
    case "already-booked":
      return "alreadyBooked";
    case "contact-invalid":
      // The server rejected the name or email. The fix is on the details step,
      // so the screen that can act on it is the one to show.
      return "failed";
    case "terms":
      /*
       * The Terms, not the booking. Stale or missing: the notice, above Terms
       * re-read and unticked. Unavailable: no notice of its own, because the
       * Terms control itself shows frame 4.5 with "Load terms again" (the
       * caller moves it there), and "We couldn't send your booking" with a
       * Try again under it would offer a send that cannot carry any Terms.
       */
      return failure.refusal === "unavailable" ? "default" : "termsChanged";
    case "refused":
      // The server decided and refused. "Nothing was booked" is TRUE here.
      return "failed";
    case "lost":
      /*
       * UNREACHABLE FROM `createBooking` UNDER 1.4, and kept anyway.
       *
       * `useBooking` now routes a lost answer to the `uncertain` STATE rather
       * than to a `failed` failure, because the attempt has a name and the
       * result can be checked. This arm survives because `BookingFailure` is
       * shared with paths that have no idempotency key and genuinely cannot
       * recover; mapping it to the recoverable screen there would offer a
       * button that resends nothing.
       */
      return "uncertain";
  }
}


/**
 * WHICH SCREEN THE REVIEW STEP SHOWS, as a pure function of four facts.
 *
 * Extracted from the component body, and the reason is that it had NO coverage
 * of any kind. Two independent passes found the same hole in the same week: an
 * independent reviewer, and QE, who proved it by rewiring
 * `identity-unavailable` to `"uncertain"` and watching all 380 book tests pass.
 * That mutation turns "nothing was sent" into "your booking may have gone
 * through" — the single most consequential sentence on this surface — plus a
 * check button that can never do anything, because `checkResult` returns
 * immediately when the hook is not in `uncertain`.
 *
 * It could not be covered where it lived. The pre-dispatch branches are
 * unreachable from the e2e harness by construction (no request is made, so no
 * stub can force them), and a component test reaches `StepReview` through its
 * `state` PROP, which is this function's output rather than its input. A pure
 * function is the only shape that can be asserted directly.
 *
 * THE ORDER IS THE SAFETY PROPERTY, not a style choice. Each branch is placed
 * where it is because the one below it would say something untrue:
 *
 *   uncertain         - a request MAY HAVE LEFT THE BROWSER, and that outranks
 *                       everything, including the network. It also outranks
 *                       every hold branch: a lost response very often takes the
 *                       countdown with it, and "that time is gone, start again"
 *                       is exactly the sentence that produces a second booking
 *                       for a request that already committed.
 *   commit failure    - the server answered; what it said decides the screen,
 *                       and a later connectivity change must not erase it.
 *   offline           - ONLY WHEN NOTHING HAS BEEN DISPATCHED. See below.
 *   pre-dispatch      - identity or the attempt could not be established, so
 *                       nothing was sent. Deliberately NOT `uncertain`.
 *   sending           - a request is on the wire, so the HOLD may not narrate
 *                       it either. See the note at that branch.
 *   hold branches     - only once nothing above has a claim to make.
 *
 * ── WHY OFFLINE MOVED DOWN ──
 *
 * It was first, and that made the screen lie. The offline copy is not a status
 * badge; it is a CLAIM ABOUT THE REQUEST — "Your booking hasn't been sent, and
 * it isn't queued — it won't go through on its own." That sentence is true of a
 * booking nobody has submitted and false of one already on the wire.
 *
 * Checked first, it produced exactly that: submit, lose the response, then walk
 * into a lift. The screen dropped its indeterminate notice — the only handle on
 * a booking that may exist — and told the visitor nothing had been sent. It did
 * the same transiently to any slow submission the moment connectivity flickered
 * mid-flight, and it could hide a definite server answer behind a later offline
 * event.
 *
 * So the network is now only the story when nothing has left. Everything that
 * implies a dispatch, or that carries a server answer, is decided first.
 */
export function reviewStateFor({
  online,
  booking,
  commitFailure,
  hold,
  afterCheck = false,
}: {
  online: boolean;
  booking: BookingState["status"];
  commitFailure: BookingFailure | null;
  hold: HoldState;
  /** The failure answered a Check after a lost answer (the booking may exist). */
  afterCheck?: boolean;
}): ReviewState {
  /*
   * HAS ANYTHING LEFT THE BROWSER?
   *
   * Enumerated over the union rather than inferred, so a new `BookingState`
   * has to be classified here deliberately. `done` and `unauthorized` are
   * rendered by `Book` before this runs and are listed for completeness: both
   * carry a server answer, and neither may be replaced by a network notice.
   */
  const dispatched =
    booking === "sending" ||
    booking === "uncertain" ||
    booking === "failed" ||
    booking === "done" ||
    booking === "unauthorized";

  if (booking === "uncertain") return "uncertain";
  /*
   * PRE-DISPATCH FAILURE USES THE EXISTING, TRUE COPY.
   *
   * "We couldn't send your booking. Nothing was booked and nothing was
   * charged." is literally accurate when identity could not be established: no
   * key was minted and no mutation left the device. "Try again" is safe for the
   * same reason — there is no committed request to duplicate.
   *
   * `cannot-start` joins it with the same copy: the attempt could not be
   * constructed (no WebCrypto, or a key outside the contract's shape), so
   * identity existed but no key and no mutation did. Different cause, identical
   * true statement.
   */
  /*
   * A TERMS REFUSAL IS ABOUT THE TERMS, AND IT DOES NOT OUTRANK THE FACTS.
   *
   * After a Check (the first send may have committed): the existing "may
   * have gone through" notice stays, with the Terms re-ask beneath it (Web
   * Director, audit WEB-TOS-03). The same key keeps the next send safe. With
   * no live hold that state blocks Confirm and offers another time, never
   * "nothing was booked" (reviewer P2).
   *
   * On a first send (nothing could have committed): with a live hold, the
   * Terms re-ask; with none, the hold branches below say what is true, and
   * Confirm is not left live over a hold that is gone (reviewer P2).
   */
  if (commitFailure?.kind === "terms") {
    if (afterCheck) return "uncertainTerms";
    if (hold.status === "held") return bookingFailureToReviewState(commitFailure);
  } else if (commitFailure) {
    /*
     * rc.11 1c, STRUCTURAL (the Web Director): after a Check the attempt is
     * unresolved, so no refusal's own copy may say "we couldn't book" or
     * "nothing was booked". Every one renders the uncertainty with the old
     * key's re-ask (the `uncertainTerms` screen: the existing
     * flow.review.uncertain.* words, Confirm under the same key), except an
     * existing booking, whose copy is true. A DEFINITE refusal never carries
     * `afterCheck` (useBooking), so it keeps its own, now true, copy.
     */
    if (afterCheck && commitFailure.kind !== "already-booked") return "uncertainTerms";
    return bookingFailureToReviewState(commitFailure);
  }

  /*
   * THE NETWORK IS THE WHOLE STORY ONLY NOW, when nothing has been dispatched.
   * `!dispatched` is redundant against the returns above for every state except
   * `sending`, and `sending` is the one that mattered: a submission in flight
   * when connectivity drops must never show the pre-dispatch sentence, not even
   * for one render.
   */
  if (!online && !dispatched) return "offline";

  if (booking === "identity-unavailable" || booking === "cannot-start") return "failed";

  /*
   * A REQUEST IS ON THE WIRE. NOTHING ABOUT THE HOLD MAY NARRATE IT.
   *
   * `dispatched` was consulted by the offline branch alone, and the hold
   * branches below were not gated on it — so a hold expiring under an in-flight
   * commit returned `"expired"`, and the screen said, simultaneously:
   *
   *     Booking…                                        (the button, aria-busy)
   *     Your held time ran out
   *     10:00 AM is open to anyone again. NOTHING WAS BOOKED — your service,
   *     day and details are all still here.              [Pick a time]
   *
   * Measured in a real browser. Reachable by pressing Confirm with the
   * countdown near zero, or on any slow response: `useHold` ticks to `expired`
   * on a CLIENT clock while the mutation is still hanging. It is the exact
   * defect this batch exists to close — a definite false claim about a request
   * that has left — in the branch that says the words out loud, and I fixed the
   * connectivity path and left this one. Review found it.
   *
   * `"default"` is the truthful answer while a send is in flight: `secondsLeft`
   * is already null so no countdown renders, the button is busy, and the SERVER
   * decides what comes next — `done`, `failed` or `uncertain`, each of which is
   * checked above this line on the very next render.
   */
  if (booking === "sending") return "default";
  if (hold.status === "expired") return "expired";
  if (hold.status === "conflict" && hold.conflict === "SLOT_TAKEN") return "taken";
  if (hold.status === "failed") return "failed";
  if (hold.status === "refused") return "paymentUnavailable";
  // NO LIVE HOLD and nothing else to say about it: a pasted URL, a refresh, or
  // an anonymous identity that went away. Last, because every branch above
  // describes a hold we DID have.
  if (hold.status !== "held") return "nohold";
  return "default";
}

/**
 * A COMMIT REFUSAL THE FLOW MAY RECOVER FROM WITH A NEW HOLD, or null.
 *
 * A definite hold-gone or slot-unavailable, including one that answered a
 * Check (`afterCheck`). That case is recoverable too — a permanent dead end
 * was the other failure (Web Director A2) — because the protection lives in
 * `useBooking.commit`: the next Confirm asks under the OLD key before any new
 * one is minted, so a booking the first send did make is shown rather than
 * duplicated.
 */
export function recoverableSlotRefusal(
  state: BookingState
): "hold-gone" | "slot-unavailable" | null {
  if (state.status !== "failed") return null;
  const kind = state.failure.kind;
  return kind === "hold-gone" || kind === "slot-unavailable" ? kind : null;
}
